import React from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { Box, Chip, Icon, IconButton, Toolbar, useMediaQuery, useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { privateRoutes } from '../routes';
import { useAuth } from '../utils/hooks/auth';
import { LogoutIcon, Typography, TypographyProps } from '../ui-kit';
import { RouteType } from '../types';
import { MenuButtonContiner, StyledAppBar } from './style';
import { ClassNamed } from './types';
import SwitchVenue from './SwitchVenue';

interface NavbarProps extends ClassNamed {
    isDrawerOpen: boolean;
    setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    shouldShowSidebar?: boolean;
}

const EnvironmentBadge = () => {
    const environment = process.env.REACT_APP_HOST_ENV as 'development' | 'demo' | 'production';
    if (environment === 'production') {
        return null;
    }

    const label = (
        <Typography uppercase variant='caption'>
            {environment === 'demo' ? 'demo' : 'dev'}
        </Typography>
    );

    return (
        <Chip
            label={label}
            color='primary'
            size='small'
        />
    );
};

const getAllRoutes = () => privateRoutes.reduce((acc: RouteType[], route) => {
    acc.push(route);

    if (route.childrenRoutes) {
        acc.push(...route.childrenRoutes);
    }
    return acc;
}, []);

const Navbar = ({ className, isDrawerOpen, shouldShowSidebar, setOpenDrawer }: NavbarProps) => {
    const { pathname } = useLocation();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

    const { t } = useTranslation();

    const currentRoute = getAllRoutes().find(({ path, exact }) => {
        return !!matchPath(pathname, { path, exact });
    });

    const pageTitle = currentRoute ? t(...currentRoute.title) : undefined;

    const { logout } = useAuth();

    const toggleDrawer = () => setOpenDrawer(!isDrawerOpen);

    let titleProps: TypographyProps = { variant: 'h6' };

    if (!isDesktop) {
        titleProps = { variant: 'body2', fontWeight: 'semiBold' };
    }

    return (
        <>
            <Toolbar />
            <StyledAppBar color="inherit" position="fixed" className={className}>
                <Toolbar>
                    {shouldShowSidebar
                     ? (
                         <MenuButtonContiner opened={isDrawerOpen}>
                             <IconButton color='inherit' onClick={toggleDrawer}>
                                 <Icon>{isDrawerOpen ? 'close' : 'menu'}</Icon>
                             </IconButton>
                         </MenuButtonContiner>
                     ): null}
                    <Box
                        display='flex'
                        flexGrow={1}
                        alignItems='center'
                        justifyContent='space-between'
                        position='relative'
                    >
                        <Box width='35%'>
                            <Typography noWrap {...titleProps}>{pageTitle}</Typography>
                        </Box>
                        <Box position='absolute' left='50%' maxWidth='30%' style={{ transform: 'translateX(-50%)' }}>
                            <SwitchVenue />
                        </Box>
                        <Box>
                            <EnvironmentBadge />
                            <IconButton color="primary" aria-label={t('Logout')} edge="end" onClick={logout}>
                                <LogoutIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </Toolbar>
            </StyledAppBar>
        </>
    );
};

export default Navbar;
